import React from 'react';
import {
    filterSelectionSummary,
    isAllFilterValues,
    isNoneFilterValues,
    toggleFilterCheckbox,
} from './insightsMatchBundleFilters';

export interface InsightsMultiSelectOption<T extends string | number> {
    value: T;
    label: string;
}

interface Props<T extends string | number> {
    label: string;
    options: InsightsMultiSelectOption<T>[];
    /** null = all; [] = none (Clear); otherwise the checked values. */
    selected: T[] | null;
    onChange: (next: T[] | null) => void;
    /** Optional: scrollable list when many matches. */
    listClassName?: string;
}

/**
 * Compact multi-select: trigger shows "Label ▾" + summary; panel is checkboxes.
 * null = all; [] = none; Select all / Clear map to those.
 */
function InsightsMultiSelectDropdown<T extends string | number>({
    label,
    options,
    selected,
    onChange,
    listClassName,
}: Props<T>): React.ReactElement | null {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const onDoc = (event: MouseEvent) => {
            if (!rootRef.current || !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (options.length === 0) return null;

    const allValues = options.map((o) => o.value);
    const all = isAllFilterValues(selected, options.length);
    const none = isNoneFilterValues(selected);
    const singleLabel =
        !all && !none && selected && selected.length === 1
            ? options.find((o) => o.value === selected[0])?.label
            : undefined;
    const summary = filterSelectionSummary(selected, options.length, singleLabel);

    return (
        <div className={`insightsMultiSelect${open ? ' isOpen' : ''}`} ref={rootRef}>
            <button
                type="button"
                className="insightsMultiSelectTrigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="insightsMultiSelectLabel">{label}</span>
                <span className="insightsMultiSelectSummary">{summary}</span>
                <span className="insightsMultiSelectCaret" aria-hidden>
                    ▾
                </span>
            </button>
            {open ? (
                <div className="insightsMultiSelectPanel" role="listbox" aria-label={label}>
                    <div className="insightsMultiSelectActions">
                        <button type="button" onClick={() => onChange(null)}>
                            Select all
                        </button>
                        <button type="button" onClick={() => onChange([])}>
                            Clear
                        </button>
                    </div>
                    <div className={`insightsMultiSelectList${listClassName ? ` ${listClassName}` : ''}`}>
                        {options.map((opt) => {
                            const checked = all || (!!selected && selected.includes(opt.value));
                            const id = `insights-ms-${label}-${String(opt.value)}`;
                            return (
                                <label key={String(opt.value)} className="insightsMultiSelectOption" htmlFor={id}>
                                    <input
                                        id={id}
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                            onChange(
                                                toggleFilterCheckbox(selected, opt.value, allValues)
                                            )
                                        }
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default InsightsMultiSelectDropdown;
